import { useState } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * StockCostHistoryPopover
 *
 * Small ⓘ button on a Stock Summary row. On open, fetches the purchase
 * history for the item and shows a compact table with each purchase's
 * date, qty, unit cost, and the running Weighted Average Cost after
 * that purchase — so the operator can see how the current cost_price
 * was arrived at (e.g. "$102.50 averaged across 2 purchases").
 *
 * Fetches on open (lazy) so it doesn't slow the Stock Summary render.
 */
export const StockCostHistoryPopover = ({ stockItemId, itemName, currentCost }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/stock-items/${stockItemId}/purchase-history?limit=20`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const receivedCount = (data?.purchases || []).filter((p) => p.status === 'Received').length;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => { setOpen(v); if (v) load(); }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`stock-cost-history-${stockItemId}`}
          title={`Show purchase history & running WAC for ${itemName}`}
          style={{
            marginLeft: '0.35rem',
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: '#e0e7ff',
            color: '#3730a3',
            border: '1px solid #c7d2fe',
            fontSize: '0.7rem',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ⓘ
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        style={{ width: '460px', maxHeight: '520px', overflow: 'auto', padding: '0.9rem 1rem' }}
        data-testid="stock-cost-history-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.15rem' }}>
          {itemName}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.6rem' }}>
          Current cost: <strong>${Number(currentCost || 0).toFixed(2)}</strong>
          {data && (
            <> · Averaged across {receivedCount} received purchase{receivedCount === 1 ? '' : 's'}
              {' '}(qty {data.current_quantity})
            </>
          )}
        </div>

        {loading && <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>}
        {err && <div style={{ padding: '0.75rem', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '0.8rem' }}>{err}</div>}

        {data && (data.purchases || []).length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
            No purchase history yet — the current cost price was set manually or via opening balance.
          </div>
        )}

        {data && (data.purchases || []).length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#475569' }}>
                <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', fontWeight: 600 }}>Supplier</th>
                <th style={{ textAlign: 'right', padding: '0.35rem 0.4rem', fontWeight: 600 }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '0.35rem 0.4rem', fontWeight: 600 }}>Unit</th>
                <th style={{ textAlign: 'right', padding: '0.35rem 0.4rem', fontWeight: 600 }}>WAC after</th>
              </tr>
            </thead>
            <tbody>
              {data.purchases.map((p) => (
                <tr
                  key={p.purchase_id + p.purchase_date}
                  style={{ borderBottom: '1px solid #f1f5f9', opacity: p.status === 'Booked' ? 0.55 : 1 }}
                  title={p.status === 'Booked' ? 'Provisional — not yet counted toward WAC' : ''}
                >
                  <td style={{ padding: '0.35rem 0.4rem', whiteSpace: 'nowrap' }}>
                    {p.purchase_date}
                    {p.status === 'Booked' && <span style={{ marginLeft: '0.25rem', fontSize: '0.65rem', color: '#94a3b8' }}>· provisional</span>}
                  </td>
                  <td style={{ padding: '0.35rem 0.4rem', color: '#334155', maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.supplier_name || '—'}
                  </td>
                  <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>{p.quantity}</td>
                  <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>${p.cost_per_unit.toFixed(2)}</td>
                  <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                    {p.status === 'Received' ? `$${p.running_wac.toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #e2e8f0', fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.5 }}>
          WAC = Weighted Average Cost. Each received purchase blends with existing stock:
          <br/>
          <em>(existing_qty × existing_cost + new_qty × new_cost) ÷ total_qty</em>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default StockCostHistoryPopover;
